import type {
  AprBlock, Block, Cell, ChipCell, Column, FormBlock, FormLine, TableBlock,
} from '@/kova/spec/types'

import { formatMinor } from '@/kova/format'

import type { ApiClient } from '@/lib/api'
import { ApiError } from '@/lib/api/errors'
import { exactMinorBig, isCurrencyCode } from '@/lib/money/money'

import type { Caller } from './handles'
import {
  payoutControls, payoutDecisionRefusal, type PayoutStanding,
} from './payoutcontrols'
import type { ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'
import { callerBeside, submitWrite, writeRefusal } from './writes'

/**
 * Referrals & Partners › Partners (`rf-partners`), Fraud flags (`rf-fraud`) and
 * Payout approvals (`rf-payouts`), read from `/v1/admin/referral/*`
 * (handler `internal/server/admin_referral.go`).
 *
 * # `expired` is not `failed`, and telling a partner the wrong one is the harm
 *
 * FR-15.2 makes an unverified partner unpayable, and the schema is emphatic
 * about the difference: `expired` "is NOT `failed` — the remedy is a refresh
 * rather than the end of the road, and telling a partner their documents were
 * rejected when they merely lapsed is both wrong and the wrong instruction".
 * The two get different words and different tones here.
 *
 * # The check that was RUN is shown beside the check that was required
 *
 * "A company signed off on a director's personal KYC is the AML failure FR-15.2
 * names, and it is only visible when the kind is shown beside the state." So
 * `check_kind` renders on the same row as `check_state`, and a company holding
 * a `kyc` is called out rather than left for somebody to notice.
 *
 * # A flag is somebody's income being questioned
 *
 * Two consequences the queue must not lose. It is **oldest first**, because
 * "the harm of leaving one open grows with time, so a newest-first queue would
 * put the oldest grievance on the last page" — and nothing here re-sorts it.
 * And every flag carries its evidence, which is required in the schema rather
 * than fetched separately: "a queue of assertions: 'partner 41 — suspicious' is
 * not reviewable and is not disputable, and it is what gets somebody's income
 * stopped by whoever has since left". A flag whose evidence array is empty is
 * therefore a row with something wrong with it, and it says so.
 *
 * # The payout queue's totals are a statement about the moment of request
 *
 * `gate_source`, `gate_check` and `gate_passed_at` are the verification AS IT
 * STOOD AT REQUEST. They are not a statement about now — the authorisation seam
 * re-reads the gate at approval and refuses one that has since expired — and
 * this view labels them as of the request rather than as current facts.
 *
 * # Law 15 — two slots, and the maker is on the row
 *
 * `requested_by` is on the queue row "so a checker knows which rows they
 * personally cannot approve before opening one". The approval block renders
 * both slots with both controls disabled: this console reads the queue and
 * approves nothing.
 *
 * # There is no beneficiary here, and there cannot be
 *
 * "THERE IS NO CREDITOR NAME AND NO IBAN, and there cannot be: the beneficiary
 * is not stored at all (FR-16.2)." Who was paid is answered by the partner —
 * our own record of the party. Nothing on this screen renders a bank detail
 * because nothing in the payload carries one.
 */

export const PARTNERS_PATH = '/referral/partners'
export const FLAGS_PATH = '/referral/flags'
export const PAYOUTS_PATH = '/referral/payouts'

const PARTNERS_SOURCE = 'postgres · referral_partners (GET /v1/admin/referral/partners)'
const FLAGS_SOURCE = 'postgres · referral_fraud_flags (GET /v1/admin/referral/flags)'
const PAYOUTS_SOURCE = 'postgres · referral_payouts (GET /v1/admin/referral/payouts)'
const AUTHORITY = 'operational · the institution’s own state wins every disagreement'

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

/**
 * An integer this console can state EXACTLY, or nothing.
 *
 * `Number.isSafeInteger`, not `Number.isInteger`, and the difference is the
 * whole function. `JSON.parse` hands back a double for a Go `int64`, so a value
 * past 2^53 has already lost its last digits by the time it arrives here —
 * `Number.isInteger(9007199254740993)` is `true`, and the number it is true
 * about is not the number the backend sent.
 *
 * Every caller of this is an id, a count or a version. Not one is an amount;
 * those go through `exactMinorBig`. So there is no case here where an
 * approximately-right integer is good enough:
 *
 *   - A COUNT wrong in its last places reads as a precise figure and is not one.
 *   - An ID wrong in its last places is a different record, and since #202 the
 *     payout id is a PATH SEGMENT on a control that approves a payment. The
 *     candidate queue made this argument first — *"the next thing that happens
 *     to a copied id is a promotion or a dismissal against it"* — and it is
 *     literally true here: the id renders as a copyable cell beside a live
 *     countersign control.
 *
 * The same rule `dashboard.ts` and `candidates.ts` already apply, spelled the
 * same way, for the same reason.
 */
function whole(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) ? value : null
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function day(at: string): string {
  return at === '' ? '' : at.slice(0, 10)
}

/** An amount and its currency, or a refusal. Laws 8 and 9. */
function amountCell(raw: unknown): Cell {
  if (!isObject(raw)) return '— no amount'
  const currency = text(raw['currency'])
  if (!isCurrencyCode(currency)) return '— no currency on this amount'
  const minor = exactMinorBig(raw['amount_minor'])
  if (minor === null) return '— not an exact integer'
  if (minor > BigInt(Number.MAX_SAFE_INTEGER) || minor < -BigInt(Number.MAX_SAFE_INTEGER)) {
    return '— beyond the exact integer range of an amount cell'
  }
  return [Number(minor), currency]
}

function amountText(raw: unknown): string {
  const cell = amountCell(raw)
  if (typeof cell === 'string') return cell
  const [minor, currency] = cell as readonly [number, string]
  return `${String(minor)} minor · ${currency}`
}

// ---------------------------------------------------------------------------
// rf-partners
// ---------------------------------------------------------------------------

interface Partner {
  readonly id: number | null
  readonly type: string
  readonly legalName: string
  readonly country: string
  readonly checkState: string
  readonly checkKind: string
  readonly passedAt: string
  readonly expiresAt: string
  readonly customerId: number | null
  readonly liveCodes: number | null
  readonly suspendedCodes: number | null
  readonly openFlags: number | null
}

function readPartner(raw: unknown): Partner | null {
  if (!isObject(raw)) return null
  const id = whole(raw['id'])
  if (id === null) return null
  return {
    id,
    type: text(raw['type']),
    legalName: text(raw['legal_name']),
    country: text(raw['country_code']),
    checkState: text(raw['check_state']),
    checkKind: text(raw['check_kind']),
    // ABSENT rather than zero when unrecorded — a zero timestamp renders as
    // the year 1.
    passedAt: text(raw['passed_at']),
    // Absent means "no expiry RECORDED", never "never expires".
    expiresAt: text(raw['expires_at']),
    // Omitted for an external marketer rather than sent as zero, which would
    // invite a link to customer zero.
    customerId: whole(raw['customer_id']),
    liveCodes: whole(raw['live_codes']),
    suspendedCodes: whole(raw['suspended_codes']),
    openFlags: whole(raw['open_flags']),
  }
}

/**
 * The verification chip.
 *
 * `expired` gets its own words and its own tone. It is the difference between
 * "refresh your documents" and "you were rejected", and only one of those is
 * true of a lapsed check.
 */
function checkChip(p: Partner): ChipCell {
  if (p.checkState === 'passed') return ['passed — payable', 'g']
  if (p.checkState === 'expired') return ['EXPIRED — needs a refresh', 'a']
  if (p.checkState === 'failed') return ['FAILED', 'r']
  if (p.checkState === 'in_progress') return ['in progress', 'n']
  if (p.checkState === 'not_started') return ['not started', 'a']
  return [p.checkState === '' ? '— not stated' : `unknown: ${p.checkState}`, 'r']
}

/** Whether a company was signed off on a natural person's check. */
function wrongKind(p: Partner): boolean {
  return p.type === 'agency' && p.checkKind === 'kyc'
}

const PARTNER_COLS: readonly Column[] = [
  ['Partner', 's', 'minmax(190px,1.4fr)'],
  ['Type', 'x', '130px'],
  ['Country', 'm', '90px'],
  ['Verification', 'c', 'minmax(190px,1.3fr)'],
  ['Check run', 'x', '150px'],
  ['Expires', 'm', '120px'],
  ['Live codes', 'n', '104px'],
  ['Open flags', 'n', '104px'],
]

function partnerRow(p: Partner): readonly Cell[] {
  return [
    [p.legalName === '' ? '— no legal name' : p.legalName, p.id === null ? '' : `partner ${String(p.id)}`],
    p.type === '' ? '— not stated' : p.type,
    p.country === '' ? '—' : p.country,
    checkChip(p),
    p.checkKind === ''
      ? '— not stated'
      : wrongKind(p)
        ? `${p.checkKind} — a company signed off on a personal check`
        : p.checkKind,
    // Absent means no expiry RECORDED, never "never expires".
    p.expiresAt === '' ? '— none recorded' : day(p.expiresAt),
    p.liveCodes === null ? '—' : String(p.liveCodes),
    p.openFlags === null ? '—' : String(p.openFlags),
  ]
}

export function composePartners(payload: Record<string, unknown>, readAt: Date): ScreenData {
  const raw = Array.isArray(payload['partners']) ? (payload['partners'] as readonly unknown[]) : []
  const rows: Partner[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readPartner(entry)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const unpayable = rows.filter((p) => p.checkState !== 'passed').length
  const expired = rows.filter((p) => p.checkState === 'expired').length
  const misKinded = rows.filter(wrongKind)

  const table: TableBlock = {
    t: 'table',
    title: 'referral_partners · verification and codes',
    src: 'ledger',
    facets: false,
    cols: PARTNER_COLS,
    rows: rows.map(partnerRow),
    footer: `${String(rows.length)} partner(s)${unpayable === 0 ? '' : ` · ${String(unpayable)} unpayable`}${expired === 0 ? '' : ` · ${String(expired)} lapsed rather than rejected`}${unreadable === 0 ? '' : ` · ${String(unreadable)} could not be read`}`,
    foot2:
      'Only a passed, evidenced and unexpired check permits a payout. `expired` is NOT `failed`: the remedy is a refresh, and telling a partner their documents were rejected when they merely lapsed is both wrong and the wrong instruction. No creditor name and no IBAN appear anywhere in this section — the beneficiary is not stored at all.',
  }

  const blocks: Block[] = [table]

  if (misKinded.length > 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'FR-15.2',
      title: `${String(misKinded.length)} company partner(s) were signed off on a personal check`,
      body: 'A company verified with a director’s personal KYC is the AML failure FR-15.2 names, and it is only visible when the check that was RUN is shown beside the state that was required. It is called out rather than left for somebody to notice by reading two columns against each other.',
      ref: misKinded.map((p) => `${p.legalName === '' ? `partner ${String(p.id ?? 0)}` : p.legalName}: ${p.checkKind}`).join(' · '),
    })
  }

  if (rows.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'n',
      kicker: 'Partners',
      title: 'No partner matched this read',
      body: 'The programme has no partners, or none matching what was asked for. An empty directory is not a failure — it is what a programme looks like before anybody joins it.',
      ref: 'referral_partners · the read returned no rows',
    })
  }

  if (unreadable > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Partial read',
      title: `${String(unreadable)} partner(s) could not be read`,
      body: 'Counted rather than dropped. A partner missing from this directory is one whose verification nobody is checking.',
      ref: 'a partner with no id identifies nobody and is refused',
    })
  }

  return {
    blocks,
    provenance: { kind: 'live', source: PARTNERS_SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt) },
    rowKeys: rows.map((p) => (p.id === null ? null : String(p.id))),
  }
}

// ---------------------------------------------------------------------------
// rf-fraud
// ---------------------------------------------------------------------------

interface Flag {
  readonly id: number | null
  readonly partnerId: number | null
  readonly code: string
  readonly signal: string
  readonly detail: string
  readonly state: string
  readonly raisedBy: string
  readonly raisedAt: string
  readonly evidenceCount: number
  readonly evidence: readonly string[]
}

function readFlag(raw: unknown): Flag | null {
  if (!isObject(raw)) return null
  const id = whole(raw['id'])
  if (id === null) return null
  const evidence: string[] = []
  if (Array.isArray(raw['evidence'])) {
    for (const e of raw['evidence'] as readonly unknown[]) {
      if (isObject(e)) {
        const kind = text(e['kind'])
        const value = text(e['value']) === '' ? text(e['detail']) : text(e['value'])
        evidence.push(kind === '' ? value : `${kind}: ${value}`)
      } else if (typeof e === 'string') {
        evidence.push(e)
      }
    }
  }
  const signalRaw = raw['signal']
  return {
    id,
    partnerId: whole(raw['partner_id']),
    code: text(raw['code']),
    signal: typeof signalRaw === 'string' ? signalRaw : isObject(signalRaw) ? text(signalRaw['kind']) : '',
    detail: text(raw['detail']),
    state: text(raw['state']),
    raisedBy: text(raw['raised_by']),
    raisedAt: text(raw['raised_at']),
    evidenceCount: evidence.length,
    evidence,
  }
}

const FLAG_COLS: readonly Column[] = [
  ['Flag', 'm', '84px'],
  ['Partner', 'm', '104px'],
  ['Code', 'm', '130px'],
  ['Signal', 'x', 'minmax(170px,1.2fr)'],
  ['Raised', 'm', '130px'],
  ['By', 'x', '140px'],
  ['Evidence', 'c', '150px'],
  ['State', 'c', '120px'],
]

function flagRow(f: Flag): readonly Cell[] {
  return [
    f.id === null ? '— no id' : String(f.id),
    f.partnerId === null ? '—' : String(f.partnerId),
    f.code === '' ? '— the partner as a whole' : f.code,
    f.signal === '' ? '— not stated' : `${f.signal}${f.detail === '' ? '' : ` · ${f.detail}`}`,
    f.raisedAt === '' ? '— not returned' : day(f.raisedAt),
    f.raisedBy === '' ? '— unattributed' : f.raisedBy,
    // An empty evidence array is a signal that something is wrong with the row:
    // the schema makes a flag without evidence unrepresentable.
    f.evidenceCount === 0
      ? (['NONE — unreviewable', 'r'] as ChipCell)
      : ([`${String(f.evidenceCount)} item(s)`, 'n'] as ChipCell),
    f.state === 'open'
      ? (['open', 'a'] as ChipCell)
      : f.state === 'confirmed'
        ? (['confirmed', 'r'] as ChipCell)
        : f.state === 'dismissed'
          ? (['dismissed', 'g'] as ChipCell)
          : ([f.state === '' ? '— not stated' : f.state, 'r'] as ChipCell),
  ]
}

export function composeFlags(payload: Record<string, unknown>, readAt: Date): ScreenData {
  const raw = Array.isArray(payload['flags']) ? (payload['flags'] as readonly unknown[]) : []
  const rows: Flag[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readFlag(entry)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const total = whole(payload['total'])
  const evidenceless = rows.filter((f) => f.evidenceCount === 0)

  const table: TableBlock = {
    t: 'table',
    title: 'referral_fraud_flags · open, oldest first',
    src: 'ledger',
    facets: false,
    cols: FLAG_COLS,
    // Never re-sorted. A flag is somebody's income being questioned and the
    // harm of leaving one open grows with time, so newest-first would put the
    // oldest grievance on the last page.
    rows: rows.map(flagRow),
    footer:
      total === null
        ? `${String(rows.length)} flag(s) on this page`
        : total > rows.length
          ? `${String(rows.length)} shown of ${String(total)} open — this is a page`
          : `${String(rows.length)} open flag(s)`,
    foot2:
      'Every flag carries the evidence that raised it, because a queue of assertions is not reviewable and not disputable — and it is what gets somebody’s income stopped by whoever has since left. The depth is the WHOLE queue, not the length of this page: a page-length count reports the cap for ever once the queue passes it.',
  }

  const blocks: Block[] = [table]

  if (evidenceless.length > 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Unreviewable',
      title: `${String(evidenceless.length)} flag(s) arrived with no evidence at all`,
      body: 'The schema makes a flag without evidence unrepresentable, so an empty array is a signal that something is wrong with the row rather than a flag somebody forgot to document. It is not reviewable and it is not disputable, and it should not be acted on until somebody explains where it came from.',
      ref: evidenceless.map((f) => `flag ${String(f.id ?? 0)}`).join(' · '),
    })
  }

  if (rows.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'g',
      kicker: 'Fraud flags',
      title: 'No flag is open',
      body: 'Nobody’s commission is currently being questioned. This is one of the few queues in the console where empty is unambiguously the good state.',
      ref: 'referral_fraud_flags · open = 0',
    })
  }

  if (unreadable > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Partial read',
      title: `${String(unreadable)} flag(s) could not be read`,
      body: 'Counted rather than dropped. A flag missing from this queue is a partner whose income is stopped with nobody reviewing why.',
      ref: 'a flag with no id is refused',
    })
  }

  blocks.push({
    t: 'note',
    tone: 'n',
    quiet: true,
      kicker: 'Read only',
    title: 'Suspending a code and lifting a suspension are not wired here',
    body: 'Both routes exist and each changes whether somebody is paid. Raising and resolving a flag are wired below.',
    ref: 'POST /referral/codes/{id}/suspend and /referral/code-suspensions/{id}/lift are untouched',
  })

  return {
    blocks,
    provenance:
      total === null
        ? {
            kind: 'partial',
            source: FLAGS_SOURCE,
            authority: AUTHORITY,
            readAt: readAtStamp(readAt),
            missing: ['how deep the whole queue is — only this page was reported'],
          }
        : { kind: 'live', source: FLAGS_SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt) },
    rowKeys: rows.map((f) => (f.id === null ? null : String(f.id))),
  }
}

// ---------------------------------------------------------------------------
// rf-payouts
// ---------------------------------------------------------------------------

interface Payout {
  readonly id: number | null
  readonly partnerId: number | null
  readonly state: string
  readonly destination: string
  readonly amount: unknown
  readonly threshold: unknown
  readonly cycleMonth: string
  readonly gateSource: string
  readonly gateCheck: string
  readonly gatePassedAt: string
  readonly requestedBy: string
  readonly requestedAt: string
  readonly requestedReason: string
  readonly approvedBy: string
  readonly approvalDecisionRef: string
  readonly idempotencyKey: string
}

function readPayout(raw: unknown): Payout | null {
  if (!isObject(raw)) return null
  const id = whole(raw['id'])
  if (id === null) return null
  return {
    id,
    partnerId: whole(raw['partner_id']),
    state: text(raw['state']),
    destination: text(raw['destination']),
    amount: raw['amount'],
    threshold: raw['threshold'],
    cycleMonth: text(raw['cycle_month']),
    gateSource: text(raw['gate_source']),
    gateCheck: text(raw['gate_check']),
    gatePassedAt: text(raw['gate_passed_at']),
    requestedBy: text(raw['requested_by']),
    requestedAt: text(raw['requested_at']),
    requestedReason: text(raw['requested_reason']),
    approvedBy: text(raw['approved_by']),
    approvalDecisionRef: text(raw['approval_decision_ref']),
    idempotencyKey: text(raw['idempotency_key']),
  }
}

function payoutStateChip(p: Payout): ChipCell {
  if (p.state === 'paid') return ['paid — the institution said so', 'g']
  if (p.state === 'submitted') return ['submitted — no confirmation yet', 'a']
  if (p.state === 'approved') return ['approved, not yet sent', 'a']
  if (p.state === 'awaiting_approval') return ['awaiting a checker', 'a']
  if (p.state === 'declined' || p.state === 'rejected') return [p.state, 'r']
  if (p.state === 'abandoned') return ['abandoned', 'n']
  return [p.state === '' ? '— not stated' : `unknown: ${p.state}`, 'r']
}

const PAYOUT_COLS: readonly Column[] = [
  ['Payout', 'm', '88px'],
  ['Partner', 'm', '104px'],
  ['Cycle', 'm', '92px'],
  ['Amount', 'a', '150px'],
  ['Threshold', 'a', '150px'],
  ['Requested by', 'x', '150px'],
  ['Verified at request', 'x', 'minmax(180px,1.2fr)'],
  ['State', 'c', 'minmax(180px,1.3fr)'],
]

function payoutRow(p: Payout): readonly Cell[] {
  return [
    p.id === null ? '— no id' : String(p.id),
    p.partnerId === null ? '—' : String(p.partnerId),
    p.cycleMonth === '' ? '—' : p.cycleMonth,
    amountCell(p.amount),
    amountCell(p.threshold),
    // The MAKER, on the row, so a checker knows which rows they personally
    // cannot approve before opening one.
    p.requestedBy === '' ? '— unattributed' : p.requestedBy,
    p.gateSource === ''
      ? '— not stated'
      : `${p.gateSource}${p.gateCheck === '' ? '' : ` · ${p.gateCheck}`}${p.gatePassedAt === '' ? '' : ` · ${day(p.gatePassedAt)}`}`,
    payoutStateChip(p),
  ]
}

/**
 * The maker-checker card for one payout awaiting approval.
 *
 * Law 15: two slots, both visible from the first render.
 *
 * # The checker slot is per-session, and it used to claim not to be
 *
 * It said *"Disabled for every session: this console reads the payout queue and
 * approves nothing"*, which was true while nothing was wired and became a lie
 * the moment something was (#202). Now it states the reason THIS operator is or
 * is not the second person: the cell, the role, the payout's state, or the one
 * that cannot be granted — that they are the person who requested it.
 *
 * `caller` is `null` only when the controls were not read at all, which is a
 * different sentence again and never an enabled slot.
 */
function approvalCard(p: Payout, caller: Caller | null): AprBlock {
  const amount = amountText(p.amount)
  const standing = payoutStanding(p)
  const checkerRefusal = standing === null
    ? 'Disabled — this payout arrived with no id this console could state exactly, so there is no payout for a countersignature to be against. A decision addressed at the wrong id is the one mistake this screen cannot let an operator make.'
    : caller === null
      ? 'Disabled — this console did not read what your session may do, so it cannot tell whether you are the second person. The permission is not assumed from silence.'
      : payoutDecisionRefusal(caller, standing, 'Countersigning a commission payout', 'Countersigning it')
  return {
    t: 'apr',
    title: `Payout ${String(p.id ?? 0)} — maker-checker`,
    summary: [
      ['Partner', p.partnerId === null ? '— not stated' : String(p.partnerId), ''],
      ['Cycle', p.cycleMonth === '' ? '— not stated' : p.cycleMonth, ''],
      ['Amount', amount, ''],
      ['Destination', p.destination === '' ? '— not stated' : p.destination, ''],
      ['Idempotency key', p.idempotencyKey === '' ? '— none carried' : p.idempotencyKey, ''],
    ],
    slots: [
      [
        'Maker',
        p.requestedBy === '' ? 'unattributed' : 'requested',
        p.requestedBy === '' ? 'r' : 'g',
        p.requestedBy === '' ? '— unattributed' : p.requestedBy,
        p.requestedAt === '' ? '—' : p.requestedAt,
        p.requestedReason === '' ? 'No reason was recorded with the request.' : p.requestedReason,
        'Requested',
        true,
        p.requestedBy === '' ? 'r' : 'g',
      ],
      [
        'Checker',
        p.approvedBy === '' ? 'awaiting' : 'approved',
        p.approvedBy === '' ? 'a' : 'g',
        p.approvedBy === '' ? 'unassigned' : p.approvedBy,
        '—',
        p.approvedBy !== ''
          ? `Approved${p.approvalDecisionRef === '' ? ', and the approval carried no decision reference — which law 13 says it should' : `, decision ${p.approvalDecisionRef}`}.`
          : checkerRefusal === ''
            ? 'You may countersign this payout: you are not the operator who requested it, and this session holds the permission. The control is below, with what it will audit and what it will not do — it does not send the money.'
            : checkerRefusal,
        'Countersign',
        p.approvedBy !== '' || checkerRefusal !== '',
        'a',
      ],
    ],
    note2:
      'Never a single approve button. The verification shown on the row is the gate AS AT REQUEST and is not a statement about now — approval re-reads it, and an expired check is refused at that seam rather than on this screen.',
  }
}

export function composePayouts(
  payload: Record<string, unknown>, readAt: Date, caller: Caller | null = null,
): ScreenData {
  const raw = Array.isArray(payload['payouts']) ? (payload['payouts'] as readonly unknown[]) : []
  const rows: Payout[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readPayout(entry)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const awaiting = rows.filter((p) => p.state === 'awaiting_approval')

  const table: TableBlock = {
    t: 'table',
    title: 'referral_payouts · oldest request first',
    src: 'ledger',
    facets: false,
    cols: PAYOUT_COLS,
    rows: rows.map(payoutRow),
    footer: `${String(rows.length)} payout(s)${awaiting.length === 0 ? '' : ` · ${String(awaiting.length)} awaiting a checker`}${unreadable === 0 ? '' : ` · ${String(unreadable)} could not be read`}`,
    foot2:
      'No creditor name and no IBAN appear here, and none can: the beneficiary is not stored at all — it is covered by the request digest and supplied again at submission. "Who was paid" is answered by the partner, our own record of the party. `paid` is only ever written from what the institution told us, never from the fact that we asked.',
  }

  const blocks: Block[] = [table]

  // One card per pending approval, never truncated: an approval left off the
  // screen to keep it short is one nobody countersigns.
  for (const p of awaiting) blocks.push(approvalCard(p, caller))

  if (awaiting.length === 0 && rows.length > 0) {
    blocks.push({
      t: 'note',
      tone: 'g',
      kicker: 'Nothing awaiting',
      title: 'No payout is waiting for a second signature',
      body: 'Every row on the queue has either been countersigned or has left the approval stage. A balance below the cycle’s threshold rolls forward, which is not a failure and is never reported as one.',
      ref: 'referral_payouts · awaiting_approval = 0',
    })
  }

  if (rows.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'n',
      kicker: 'Payout approvals',
      title: 'No payout has been requested',
      body: 'A commission balance below the cycle’s minimum rolls forward rather than being paid, so an empty queue is the ordinary state of a young programme — not a sign that something failed to run.',
      ref: 'referral_payouts · the read returned no rows',
    })
  }

  if (unreadable > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Partial read',
      title: `${String(unreadable)} payout(s) could not be read`,
      body: 'Counted rather than dropped. A payout missing from this queue is a partner waiting on money with nobody able to countersign it.',
      ref: 'a payout with no id is refused',
    })
  }

  return {
    blocks,
    provenance: { kind: 'live', source: PAYOUTS_SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt) },
    rowKeys: rows.map((p) => (p.id === null ? null : String(p.id))),
  }
}

// ---------------------------------------------------------------------------
// Reading
// ---------------------------------------------------------------------------

function unreadableScreen(what: string, source: string, reason: string, decisionRef: string | null): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: what,
      title: `${what} could not be read`,
      body: `${reason} Nothing below is known. On this section an unread screen must not be mistaken for a quiet one: a partner’s verification, a flag against their income and a payout waiting on a signature all look identical to an empty screen.`,
      ref: decisionRef === null ? 'Nothing was read.' : `decision_ref ${decisionRef}`,
    }],
    provenance: { kind: 'unread', source, reason },
  }
}

/**
 * A section read, and optionally the controls that act on what it read.
 *
 * `controls` is read BESIDE the screen rather than after it, and its failure
 * cannot fail the screen: `readCaller` resolves every error to "not held", so a
 * `/me` that is down leaves the tables fully readable and every control dead
 * with the reason said out loud. That is the trade this console wants — the
 * read is what an operator needs most often, and losing it because a permission
 * could not be resolved would be the wrong way round.
 */
function referralLoader(
  what: string,
  path: string,
  source: string,
  compose: (
    payload: Record<string, unknown>, readAt: Date, caller: Caller | null,
  ) => ScreenData,
  controls?: (api: ApiClient, caller: Caller, payload: Record<string, unknown>) => readonly Block[],
): ScreenLoader {
  return async (api) => {
    let payload: unknown
    const who = controls === undefined ? null : callerBeside(api)
    try {
      payload = await api.get<unknown>(path)
    } catch (error) {
      if (error instanceof ApiError) {
        return unreadableScreen(
          what,
          source,
          `The endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
          error.decisionRef,
        )
      }
      if (error instanceof DOMException && error.name === 'AbortError') throw error
      return unreadableScreen(what, source, 'The backend could not be reached.', null)
    }
    if (!isObject(payload)) {
      return unreadableScreen(what, source, 'The endpoint returned a shape this console does not know.', null)
    }
    // Awaited BEFORE `compose` rather than after it, because on this section a
    // read is not only what fills a table: the payout queue's approval card
    // states who may countersign, and it cannot say that from a promise. A
    // screen with no controls passes `null` and composes exactly as before.
    const caller = who === null ? null : await who
    const screen = compose(payload, new Date(), caller)
    if (controls === undefined || caller === null) return screen
    return { ...screen, blocks: [...screen.blocks, ...controls(api, caller, payload)] }
  }
}

export const loadReferralPartners = referralLoader('Partners', PARTNERS_PATH, PARTNERS_SOURCE, composePartners)
export const loadReferralFlags = referralLoader(
  'Fraud flags', FLAGS_PATH, FLAGS_SOURCE, composeFlags,
  (api, caller, payload) => {
    const open = openFlags(Array.isArray(payload['flags']) ? payload['flags'] : [])
    return [raiseFlagForm(api, caller), resolveFlagForm(api, caller, open)]
  },
)
export const loadReferralPayouts = referralLoader(
  'Payout approvals', PAYOUTS_PATH, PAYOUTS_SOURCE, composePayouts,
  (api, caller, payload) => payoutControls(api, caller, awaitingApproval(payload)),
)

/**
 * The payouts still waiting for a second person, as the controls need them.
 *
 * Derived from the same payload the table was drawn from, and filtered on the
 * same predicate, so a row an operator can see and a control they can use
 * cannot disagree about which payouts are open.
 *
 * A payout with no readable id is dropped rather than defaulted: `id` is the
 * path segment, and an id this console could not state exactly would decide a
 * DIFFERENT payout. The read above already counts it as unreadable and says so
 * on screen, which is where an operator finds out that a control is missing.
 */
export function awaitingApproval(payload: Record<string, unknown>): readonly PayoutStanding[] {
  const raw = Array.isArray(payload['payouts']) ? (payload['payouts'] as readonly unknown[]) : []
  const out: PayoutStanding[] = []
  for (const entry of raw) {
    const p = readPayout(entry)
    if (p === null || p.state !== 'awaiting_approval') continue
    const standing = payoutStanding(p)
    if (standing !== null) out.push(standing)
  }
  return out
}

/**
 * One payout, as much of it as a control is allowed to know — or nothing.
 *
 * `null` on a payout with no stateable id, and NOT a zero. `readPayout` already
 * refuses such a row, so this is unreachable today — which is exactly why it is
 * worth spelling: `id ?? 0` would compose `/referral/payouts/0/approve`, a path
 * the backend refuses only because its minimum is 1. A control addressed at a
 * payout that does not exist should not be composed at all rather than rely on
 * a range check at the other end.
 */
function payoutStanding(p: Payout): PayoutStanding | null {
  if (p.id === null) return null
  return {
    id: p.id,
    state: p.state,
    requestedBy: p.requestedBy,
    amount: amountText(p.amount),
    partner: p.partnerId === null ? 'a partner this read could not name' : `partner ${String(p.partnerId)}`,
  }
}

// ---------------------------------------------------------------------------
// One partner, opened from a row — GET /v1/admin/referral/partners/{partnerID}
// ---------------------------------------------------------------------------

/**
 * A partner's whole conduct history.
 *
 * The directory row says how many flags are open. This says which, and what
 * was behind each — and it returns RESOLVED flags and LIFTED suspensions as
 * well as live ones, which is the point: a partner whose code was suspended
 * twice and reinstated twice looks identical to one who has never been touched
 * if a console shows only what is live now.
 *
 * Hangs off a row rather than a screen. See `openers.ts`.
 */
export function partnerPath(partnerID: string): string {
  return `${PARTNERS_PATH}/${encodeURIComponent(partnerID)}`
}

const PARTNER_SOURCE = 'postgres · referral_partners + flags + suspensions (GET /v1/admin/referral/partners/{id})'

const SUSPENSION_COLS: readonly Column[] = [
  ['Code', 'm', '130px'],
  ['Suspended', 'm', '130px'],
  ['By', 'x', '150px'],
  ['Reason', 'x', 'minmax(200px,1.4fr)'],
  ['Accruals', 'c', '170px'],
  ['Still in force', 'c', '140px'],
]

function suspensionRow(raw: unknown): readonly Cell[] | null {
  if (!isObject(raw)) return null
  const live = typeof raw['live'] === 'boolean' ? raw['live'] : null
  const preserved = typeof raw['accruals_preserved'] === 'boolean' ? raw['accruals_preserved'] : null
  return [
    text(raw['code']) === '' ? '— not stated' : text(raw['code']),
    day(text(raw['suspended_at'])) === '' ? '— not returned' : day(text(raw['suspended_at'])),
    text(raw['suspended_by']) === '' ? '— unattributed' : text(raw['suspended_by']),
    `${text(raw['reason_code']) === '' ? '— no code' : text(raw['reason_code'])}${text(raw['reason']) === '' ? '' : ` · ${text(raw['reason'])}`}`,
    // Whether the commission already earned survives the suspension is the
    // question a partner asks first, so it is a column rather than a detail.
    preserved === null
      ? (['— not stated', 'r'] as ChipCell)
      : preserved
        ? (['earned commission kept', 'g'] as ChipCell)
        : (['earned commission withheld', 'r'] as ChipCell),
    live === null
      ? (['— not stated', 'r'] as ChipCell)
      : live
        ? (['in force', 'r'] as ChipCell)
        : (['lifted', 'n'] as ChipCell),
  ]
}


const ACCRUAL_COLS: readonly Column[] = [
  ['Accrual', 'm', '96px'],
  ['Kind', 'x', 'minmax(170px,1.2fr)'],
  ['Partner’s reference', 'm', 'minmax(170px,1.2fr)'],
  ['Amount', 'a', '150px'],
  ['Uncapped', 'a', '150px'],
  ['Scheme', 'm', '140px'],
  ['Accrued', 'm', '130px'],
]

/**
 * One accrued commission.
 *
 * `partner_reference` is what the PARTNER calls this referral — "their
 * dashboard never shows a customer id, it shows this, so this is the only
 * string they can quote". An operator answering a partner's question needs the
 * string the partner has, which is why it is a column rather than a detail.
 *
 * `uncapped` beside `amount` is the scheme's cap doing its work: the two differ
 * exactly when a cap bound, and showing only the paid figure hides the reason a
 * partner's expectation and our record disagree.
 */
function accrualRow(raw: unknown): readonly Cell[] | null {
  if (!isObject(raw)) return null
  const kind = text(raw['Kind']) === '' ? text(raw['kind']) : text(raw['Kind'])
  const uncapped = raw['uncapped']
  return [
    whole(raw['id']) === null ? '— no id' : String(whole(raw['id'])),
    kind === 'activation_bounty'
      ? 'activation bounty — qualified activation only'
      : kind === 'revenue_share'
        ? 'revenue share — net fee revenue'
        : kind === '' ? '— not stated' : kind,
    // The only string a partner can quote back at us.
    text(raw['partner_reference']) === '' ? '— none' : text(raw['partner_reference']),
    amountCell(raw['amount']),
    uncapped === undefined ? '— not stated' : amountCell(uncapped),
    `${text(raw['scheme_id']) === '' ? String(whole(raw['scheme_id']) ?? '—') : text(raw['scheme_id'])} v${String(whole(raw['scheme_version']) ?? 0)}`,
    day(text(raw['accrued_at'])) === '' ? '— not returned' : day(text(raw['accrued_at'])),
  ]
}

const CLAWBACK_COLS: readonly Column[] = [
  ['Clawback', 'm', '96px'],
  ['Accrual', 'm', '96px'],
  ['Amount', 'a', '150px'],
  ['Reason', 'x', 'minmax(200px,1.4fr)'],
  ['By', 'x', '150px'],
  ['decision_ref', 'm', '176px'],
  ['Still in force', 'c', '130px'],
]

/**
 * One accrued commission the operator decided not to pay.
 *
 * The amount is the accrual's, WHOLE — there is no partial clawback. And every
 * one carries a `decision_ref`, because deciding not to pay somebody is an
 * authorisation decision like any other (law 13): the reference resolves in the
 * decision explorer, which is what makes the decision disputable.
 */
function clawbackRow(raw: unknown): readonly Cell[] | null {
  if (!isObject(raw)) return null
  const live = typeof raw['live'] === 'boolean' ? raw['live'] : null
  const currency = text(raw['currency'])
  return [
    whole(raw['id']) === null ? '— no id' : String(whole(raw['id'])),
    whole(raw['accrual_id']) === null ? '—' : String(whole(raw['accrual_id'])),
    amountCell({ amount_minor: raw['amount_minor'], currency }),
    `${text(raw['reason_code']) === '' ? '— no code' : text(raw['reason_code'])}${text(raw['reason']) === '' ? '' : ` · ${text(raw['reason'])}`}`,
    text(raw['clawed_back_by']) === '' ? '— unattributed' : text(raw['clawed_back_by']),
    // Law 13. Deciding not to pay somebody is a decision they can dispute, and
    // a clawback with no reference is one they cannot.
    text(raw['decision_ref']) === '' ? '— NONE (law 13)' : text(raw['decision_ref']),
    live === null
      ? (['— not stated', 'r'] as ChipCell)
      : live
        ? (['in force', 'r'] as ChipCell)
        : (['reversed', 'g'] as ChipCell),
  ]
}

async function partnerLedger(
  api: ApiClient,
  partnerID: string,
  path: string,
  key: string,
): Promise<readonly unknown[] | null> {
  try {
    const payload = await api.get<unknown>(`${partnerPath(partnerID)}/${path}`)
    if (!isObject(payload)) return null
    const list = payload[key]
    return Array.isArray(list) ? (list as readonly unknown[]) : null
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return null
  }
}


const TAX_COLS: readonly Column[] = [
  ['Version', 'm', '90px'],
  ['Country', 'm', '90px'],
  ['Status', 'x', 'minmax(180px,1.3fr)'],
  ['Tax identifier', 'm', 'minmax(170px,1.2fr)'],
  ['Effective from', 'm', '130px'],
  ['In force', 'c', '170px'],
  ['Recorded by', 'x', 'minmax(150px,1.1fr)'],
]

/**
 * One recorded tax position, effective-dated.
 *
 * "So an invoice issued in March reproduces against March's status rather than
 * today's." That is why superseded versions stay: a self-billing invoice cites
 * the position that governed when it was issued, and a list of only the current
 * one cannot explain a document somebody is holding.
 *
 * `live` and `governs_now` are the pair this codebase has met four times now,
 * and it means the same thing here — a position recorded to take effect next
 * quarter is open and governs nothing.
 */
function taxRow(raw: unknown): readonly Cell[] | null {
  if (!isObject(raw)) return null
  const version = whole(raw['version'])
  if (version === null) return null
  const governs = typeof raw['governs_now'] === 'boolean' ? raw['governs_now'] : null
  const live = typeof raw['live'] === 'boolean' ? raw['live'] : null
  const id = text(raw['tax_identifier'])
  return [
    `v${String(version)}`,
    text(raw['country']) === '' ? '—' : text(raw['country']),
    text(raw['status']) === '' ? '— not stated' : text(raw['status']),
    // An identifier, not a credential: it is the number printed on an invoice.
    id === '' ? '— none recorded' : id,
    day(text(raw['effective_from'])) === '' ? '— not stated' : day(text(raw['effective_from'])),
    governs === true
      ? (['in force now', 'g'] as ChipCell)
      : governs === false
        ? (live === true ? (['recorded, not yet in force', 'a'] as ChipCell) : (['superseded', 'n'] as ChipCell))
        : (['— not stated', 'r'] as ChipCell),
    `${text(raw['recorded_by']) === '' ? '— unattributed' : text(raw['recorded_by'])}${text(raw['reason_code']) === '' ? '' : ` · ${text(raw['reason_code'])}`}`,
  ]
}

// ---------------------------------------------------------------------------
// The self-billing invoices issued to one partner
// ---------------------------------------------------------------------------

/**
 * Every self-billing invoice raised on this partner's behalf, read from
 * `GET /v1/admin/referrals/partners/{partner_id}/self-billing-invoices`.
 *
 * # Why it hangs off the PARTNER and not the payout row
 *
 * #103 put this on a payout row and #105 found the address was the issue route.
 * #109 then proposed reaching the document from the payout queue through the
 * partner — and the backend had already refused that join in the endpoint's own
 * description: "NOT KEYED BY PAYOUT... The approval queue returns only payouts
 * awaiting approval and an invoice is only issued for one already paid, so an
 * invoice id hung on the queue row would be null on every row that can appear
 * there." The two state sets are disjoint, so no row on `rf-payouts` can ever
 * have an invoice, and a screen built on that join would pass its own tests and
 * return nothing in production. The partner is where the documents are, which
 * is why `rf-payouts` no longer opens into anything.
 *
 * # One call, and deliberately not two
 *
 * Each entry is whole — "the same shape the id-keyed read returns, including
 * the canonical rendering and its digest" — so the list alone answers the
 * screen. The per-invoice read at `/referrals/self-billing-invoices/{id}` is
 * not called: it would be a second round trip for bytes already held.
 *
 * # The digest is shown; the document is not re-drawn
 *
 * `document` is the exact bytes the partner downloads and the exact bytes the
 * hash is taken over. Re-rendering it as HTML here would produce a SECOND
 * artefact that could differ from the copy the partner holds, and the copy is
 * the thing under dispute in the conversation this screen exists to settle.
 * `content_hash` is what a held copy is checked against, so the hash renders
 * and the bytes never do.
 *
 * # `gross_minor` equals the payout amount EXACTLY
 *
 * "The payment was executed at the institution before the document could exist,
 * so any VAT is contained within that amount rather than added to it." A
 * console showing net plus VAT as though the partner would additionally receive
 * the tax would describe a payment that never happened, so the three figures
 * are rendered together with that sentence beside them.
 *
 * # Three states, and none of them may render as another
 *
 * A partner with no invoices is ORDINARY: a payout is executed before the
 * document exists, so a partner awaiting approval or never paid legitimately
 * has none. A failed read is a real failure. Both are stated as note blocks
 * rather than left to provenance — the header badge is the SCREEN loader's, and
 * a row-opened read's provenance is never rendered anywhere (`Console.tsx`
 * consumes `rowRead.data.blocks` and nothing else), so an absence expressed
 * only in provenance is an absence an operator cannot see. Law 20.
 *
 * # Newest first, as returned
 *
 * The backend orders the list and nothing here re-sorts it.
 */
export function selfBillingInvoicesPath(partnerID: string): string {
  return `/referrals/partners/${encodeURIComponent(partnerID)}/self-billing-invoices`
}

const INVOICE_COLS: readonly Column[] = [
  ['Invoice', 'm', 'minmax(150px,1.1fr)'],
  ['Issued', 'm', '120px'],
  ['Payout', 'm', '96px'],
  ['Treatment', 'x', 'minmax(190px,1.3fr)'],
  ['Gross — the payout', 'a', '160px'],
  ['Document digest', 'm', 'minmax(180px,1.3fr)'],
]

const LINE_COLS: readonly Column[] = [
  ['Description', 'x', 'minmax(240px,2fr)'],
  ['Net', 'a', '150px'],
  ['VAT', 'a', '150px'],
  ['Gross', 'a', '150px'],
]

/** The document's own reference, or a stand-in that still names the row. */
function invoiceName(raw: Record<string, unknown>): string {
  const number = text(raw['invoice_number'])
  if (number !== '') return number
  const id = whole(raw['id'])
  return id === null ? '— no number' : `— no number (invoice ${String(id)})`
}

/**
 * The treatment and the rate that produced the VAT, read together.
 *
 * A rate without its treatment is unreadable — 0 bps means one thing under
 * `reverse_charge` and something else entirely under `exempt` — so neither is
 * rendered alone.
 */
function invoiceTreatment(raw: Record<string, unknown>): string {
  const treatment = text(raw['treatment'])
  const bps = whole(raw['rate_bps'])
  return `${treatment === '' ? '— not stated' : treatment} · ${bps === null ? 'no rate stated' : `${String(bps)} bps`}`
}

/** SHA-256 of the stored rendering, or a refusal. A malformed digest checks nothing. */
function invoiceDigest(raw: Record<string, unknown>): string {
  const hash = text(raw['content_hash'])
  if (hash === '') return '— none recorded'
  return /^[0-9a-f]{64}$/.test(hash) ? hash : '— not a SHA-256 digest'
}

function invoiceRow(raw: unknown): readonly Cell[] | null {
  if (!isObject(raw)) return null
  if (whole(raw['id']) === null) return null
  const currency = text(raw['currency'])
  return [
    invoiceName(raw),
    day(text(raw['issued_at'])) === '' ? '— not stated' : day(text(raw['issued_at'])),
    whole(raw['payout_id']) === null ? '— none' : String(whole(raw['payout_id'])),
    invoiceTreatment(raw),
    amountCell({ amount_minor: raw['gross_minor'], currency }),
    invoiceDigest(raw),
  ]
}

/**
 * One invoice, as a document rather than as a row.
 *
 * The totals are rendered through the money module's own formatter, so the
 * minor units are scaled by the ISO-4217 exponent for the invoice's currency
 * rather than by an assumed two places (law 9), and the code stays adjacent to
 * the number rather than merged into it (law 8).
 */
function invoiceDetail(raw: Record<string, unknown>): readonly Block[] {
  const currency = text(raw['currency'])
  const named = raw['names_customers_by_id']

  const total = (key: string): string => {
    const minor = exactMinorBig(raw[key])
    if (minor === null) return '— not an exact integer'
    if (!isCurrencyCode(currency)) return '— no currency on this invoice'
    return `${formatMinor(minor, currency)} ${currency}`
  }

  const supplierTaxID = text(raw['supplier_tax_identifier'])

  const blocks: Block[] = [{
    t: 'kv',
    title: `Self-billing invoice ${invoiceName(raw)}`,
    items: [
      ['issued',
        day(text(raw['issued_at'])) === '' ? '— not stated' : day(text(raw['issued_at'])),
        '', 'n',
        `The tax point, by ${text(raw['issued_by']) === '' ? 'nobody recorded' : text(raw['issued_by'])}. The rule and the profile were resolved as at this instant rather than at now(), which is what lets a document issued in March be reproduced in July.`,
        'm'],
      ['supplier — the partner',
        `${text(raw['supplier_legal_name']) === '' ? '— not stated' : text(raw['supplier_legal_name'])}${text(raw['supplier_country']) === '' ? '' : ` · ${text(raw['supplier_country'])}`}`,
        text(raw['supplier_status']) === '' ? '' : text(raw['supplier_status']), 'n',
        'Both establishments travel by value on the document, so the treatment can be reproduced without a second lookup.',
        ''],
      // The number a self-billing arrangement legally turns on: we raise the
      // document in the supplier's name, so the supplier's tax identifier is
      // what makes it their invoice rather than our note. It is optional on the
      // payload, and legitimately absent for a non-taxable person — which is a
      // different fact from a missing field and says so. An identifier printed
      // on a document, never a credential (law 11 is about secrets, and this is
      // not one).
      ['supplier tax identifier',
        supplierTaxID === '' ? '— none recorded' : supplierTaxID,
        '', 'n',
        'Absent is a real answer here: a non-taxable person has none, and a self-billing invoice raised for one carries no VAT number because there is none to carry.',
        'm'],
      ['issuer — us',
        `${text(raw['issuer_country']) === '' ? '— not stated' : text(raw['issuer_country'])}${text(raw['issuer_tax_identifier']) === '' ? '' : ` · ${text(raw['issuer_tax_identifier'])}`}`,
        '', 'n',
        'Our own establishment and VAT number as they were printed on this document.',
        'm'],
      ['treatment', invoiceTreatment(raw), '', 'n',
        `Cited by row and version: rule ${String(whole(raw['rule_id']) ?? 0)} v${String(whole(raw['rule_version']) ?? 0)} · tax position ${String(whole(raw['profile_id']) ?? 0)} v${String(whole(raw['profile_version']) ?? 0)}. Both are in the tax positions table above, at the version that governed then rather than the one in force now.`,
        'm'],
      ['wording',
        text(raw['invoice_note']) === '' ? '— none' : text(raw['invoice_note']),
        '', 'n',
        'Carried by value, so the sentence printed on a document issued in March is the sentence it still reproduces in July.',
        ''],
      ['totals',
        `${total('net_minor')} net · ${total('vat_minor')} VAT · ${total('gross_minor')} gross`,
        'gross equals the payout', 'a',
        'The gross equals the payout amount exactly. The payment was executed at the institution before this document could exist, so any VAT is contained within that amount rather than added to it — the partner received the gross, not the gross plus tax.',
        ''],
      ['document digest', invoiceDigest(raw), 'not re-rendered', 'n',
        `SHA-256 of the stored rendering, re-verified by the backend on every read. The document itself is deliberately not drawn here: a second rendering could differ from the copy the partner holds, and this is what that copy is checked against. Document format version ${String(whole(raw['rendering_version']) ?? 0)}.`,
        'm'],
      ['names our customers by id',
        named === true ? 'yes' : named === false ? 'no' : '— not stated', '', 'n',
        'The backend computes what the format version means, so this console does not encode which versions are which. Where it is “no”, the document names each referral by the opaque reference the partner’s own dashboard shows them.',
        ''],
    ],
  }]

  const rawLines = Array.isArray(raw['lines']) ? (raw['lines'] as readonly unknown[]) : []
  blocks.push({
    t: 'table',
    title: `Self-billing invoice ${invoiceName(raw)} · lines`,
    src: 'ledger',
    facets: false,
    cols: LINE_COLS,
    rows: rawLines.filter(isObject).map((line) => [
      text(line['description']) === '' ? '— no description' : text(line['description']),
      amountCell({ amount_minor: line['net_minor'], currency }),
      amountCell({ amount_minor: line['vat_minor'], currency }),
      amountCell({ amount_minor: line['gross_minor'], currency }),
    ] as readonly Cell[]),
    footer: `${String(rawLines.length)} line(s)`,
    foot2:
      'The tax is computed per line rather than on the total and allocated back, which is where a cent goes missing. Every amount carries the invoice’s currency and none is a float.',
  })

  return blocks
}

type InvoiceRead =
  | { readonly ok: true; readonly invoices: readonly unknown[] }
  | { readonly ok: false; readonly reason: string; readonly ref: string }

/**
 * Read the partner's invoices, keeping "it failed" apart from "there are none".
 *
 * The tax-profiles read above collapses a thrown call and an unrecognised
 * payload into one `null`, and renders nothing at all for an empty list. Both
 * are wrong for this list: an empty one is the common, legitimate state and has
 * to be visible as such, so the outcomes stay distinguishable here.
 */
async function partnerInvoices(api: ApiClient, partnerID: string): Promise<InvoiceRead> {
  try {
    const payload = await api.get<unknown>(selfBillingInvoicesPath(partnerID))
    if (!isObject(payload) || !Array.isArray(payload['invoices'])) {
      return {
        ok: false,
        reason: 'The invoice list returned a shape this console does not know.',
        ref: `partner asked for: ${partnerID}`,
      }
    }
    return { ok: true, invoices: payload['invoices'] as readonly unknown[] }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    if (error instanceof ApiError) {
      return {
        ok: false,
        // A 500 is worth naming rather than generalising. The backend
        // re-computes every rendering and compares it with the digest recorded
        // at issue, and one mismatch fails the WHOLE response rather than
        // returning the rest — because a list quietly missing the one document
        // something has gone wrong with would read as "never issued". So this
        // status can mean a stored document no longer matches what was issued.
        reason: `The invoice list answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.${error.status === 500 ? ' The backend verifies every document against its recorded digest and fails the whole list rather than returning the rest, so a server error here can mean a stored rendering no longer matches the one that was issued — worth escalating rather than retrying.' : ''}`,
        ref: error.decisionRef === null ? `partner asked for: ${partnerID}` : `decision_ref ${error.decisionRef}`,
      }
    }
    return { ok: false, reason: 'The backend could not be reached.', ref: `partner asked for: ${partnerID}` }
  }
}

export async function readPartnerDetail(api: ApiClient, partnerID: string): Promise<ScreenData> {
  const wanted = partnerID.trim()
  if (wanted === '') {
    const reason = 'No partner id was given, so nothing was looked up.'
    return {
      blocks: [{ t: 'note', tone: 'r', kicker: 'Partner', title: 'No partner was requested', body: reason, ref: 'a row with no id identifies nobody' }],
      provenance: { kind: 'unread', source: PARTNER_SOURCE, reason },
    }
  }

  let payload: unknown
  try {
    payload = await api.get<unknown>(partnerPath(wanted))
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    const reason = error instanceof ApiError
      ? `The partner endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`
      : 'The backend could not be reached.'
    return {
      blocks: [{
        t: 'note', tone: 'r', kicker: 'Partner',
        title: `Partner ${wanted} could not be read`,
        body: `${reason} No flag and no suspension below is known — which is different from this partner having a clean record.`,
        ref: error instanceof ApiError && error.decisionRef !== null ? `decision_ref ${error.decisionRef}` : `partner asked for: ${wanted}`,
      }],
      provenance: { kind: 'unread', source: PARTNER_SOURCE, reason },
    }
  }

  if (!isObject(payload)) {
    const reason = 'The partner endpoint returned a shape this console does not know.'
    return {
      blocks: [{ t: 'note', tone: 'r', kicker: 'Partner', title: `Partner ${wanted} could not be read`, body: reason, ref: `partner asked for: ${wanted}` }],
      provenance: { kind: 'unread', source: PARTNER_SOURCE, reason },
    }
  }

  const flags: Flag[] = []
  let unreadableFlags = 0
  if (Array.isArray(payload['flags'])) {
    for (const entry of payload['flags'] as readonly unknown[]) {
      const parsed = readFlag(entry)
      if (parsed === null) unreadableFlags += 1
      else flags.push(parsed)
    }
  }

  const suspensions: (readonly Cell[])[] = []
  let unreadableSuspensions = 0
  if (Array.isArray(payload['suspensions'])) {
    for (const entry of payload['suspensions'] as readonly unknown[]) {
      const parsed = suspensionRow(entry)
      if (parsed === null) unreadableSuspensions += 1
      else suspensions.push(parsed)
    }
  }

  const open = flags.filter((f) => f.state === 'open').length
  const live = suspensions.filter((r) => JSON.stringify(r[5]) === JSON.stringify(['in force', 'r'])).length

  const blocks: Block[] = [
    {
      t: 'table',
      title: `Conduct history — partner ${wanted} · flags`,
      src: 'ledger',
      facets: false,
      cols: FLAG_COLS,
      rows: flags.map(flagRow),
      footer: `${String(flags.length)} flag(s)${open === 0 ? '' : ` · ${String(open)} open`}${unreadableFlags === 0 ? '' : ` · ${String(unreadableFlags)} could not be read`}`,
      foot2:
        'Resolved flags are here as well as open ones. A partner who was flagged twice and cleared twice looks identical to one nobody has ever questioned if a console shows only what is live now — and the second is the one whose record should be clean.',
    },
    {
      t: 'table',
      title: `Conduct history — partner ${wanted} · code suspensions`,
      src: 'ledger',
      facets: false,
      cols: SUSPENSION_COLS,
      rows: suspensions,
      footer: `${String(suspensions.length)} suspension(s)${live === 0 ? '' : ` · ${String(live)} still in force`}${unreadableSuspensions === 0 ? '' : ` · ${String(unreadableSuspensions)} could not be read`}`,
      foot2:
        'Lifted suspensions are kept for the same reason resolved flags are. Whether commission already earned survived a suspension is its own column, because it is the question a partner asks first.',
    },
  ]

  if (flags.length === 0 && suspensions.length === 0 && unreadableFlags === 0 && unreadableSuspensions === 0) {
    blocks.push({
      t: 'note',
      tone: 'g',
      kicker: 'Clean record',
      title: 'This partner has never been flagged and no code of theirs has been suspended',
      body: 'And that is a read, not an absence: the endpoint returns resolved flags and lifted suspensions as well as live ones, so an empty history here means nothing has ever been raised rather than that nothing is raised today.',
      ref: `partner ${wanted}`,
    })
  }

  // The money beside the conduct. Read after the partner, and each failing
  // independently: a partner whose flags read and whose accruals did not is a
  // partial answer that says so, not a screen that collapses.
  const accrualsRaw = await partnerLedger(api, wanted, 'accruals', 'accruals')
  const clawbacksRaw = await partnerLedger(api, wanted, 'clawbacks', 'clawbacks')

  const missing: string[] = []

  if (accrualsRaw === null) missing.push('what this partner has accrued')
  else {
    const rows: (readonly Cell[])[] = []
    let bad = 0
    for (const entry of accrualsRaw) {
      const parsed = accrualRow(entry)
      if (parsed === null) bad += 1
      else rows.push(parsed)
    }
    blocks.push({
      t: 'table',
      title: `Commission accrued — partner ${wanted}`,
      src: 'ledger',
      facets: false,
      cols: ACCRUAL_COLS,
      rows,
      footer: `${String(rows.length)} accrual(s)${bad === 0 ? '' : ` · ${String(bad)} could not be read`}`,
      foot2:
        'Signing up alone never pays: an activation bounty is earned on QUALIFIED activation — identity checks complete and a first real payout at or above the scheme’s minimum — and a revenue share is a percentage of fees actually collected, net of refunds. The partner’s own reference is here because it is the only string their dashboard shows them, and the only one they can quote.',
    })
  }

  if (clawbacksRaw === null) missing.push('what has been clawed back from this partner')
  else {
    const rows: (readonly Cell[])[] = []
    let bad = 0
    for (const entry of clawbacksRaw) {
      const parsed = clawbackRow(entry)
      if (parsed === null) bad += 1
      else rows.push(parsed)
    }
    if (rows.length > 0 || bad > 0) {
      blocks.push({
        t: 'table',
        title: `Clawed back — partner ${wanted}`,
        src: 'ledger',
        facets: false,
        cols: CLAWBACK_COLS,
        rows,
        footer: `${String(rows.length)} clawback(s)${bad === 0 ? '' : ` · ${String(bad)} could not be read`}`,
        foot2:
          'The amount is the accrual’s, whole — there is no partial clawback. A clawback can only ever reach a commission that has not been paid: recovering a paid one is a movement between a third party and the operator, which must be executed at the institution first and posted from the resulting event. Reversed clawbacks stay here, because a reversal is part of the record.',
      })
    }
  }

  // The tax positions this partner's invoices are issued against. A different
  // path prefix from the rest of the partner reads (`/referrals/` rather than
  // `/referral/`), which is the backend's and not a typo here.
  let taxRaw: readonly unknown[] | null = null
  try {
    const payload = await api.get<unknown>(`/referrals/partners/${encodeURIComponent(wanted)}/tax-profiles`)
    taxRaw = isObject(payload) && Array.isArray(payload['tax_profiles'])
      ? (payload['tax_profiles'] as readonly unknown[])
      : isObject(payload) && Array.isArray(payload['profiles'])
        ? (payload['profiles'] as readonly unknown[])
        : null
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    taxRaw = null
  }

  if (taxRaw === null) missing.push('the tax positions this partner’s invoices are issued against')
  else if (taxRaw.length > 0) {
    const rows: (readonly Cell[])[] = []
    let bad = 0
    for (const entry of taxRaw) {
      const parsed = taxRow(entry)
      if (parsed === null) bad += 1
      else rows.push(parsed)
    }
    blocks.push({
      t: 'table',
      title: `Tax positions — partner ${wanted}`,
      src: 'ledger',
      facets: false,
      cols: TAX_COLS,
      rows,
      footer: `${String(rows.length)} position(s)${bad === 0 ? '' : ` · ${String(bad)} could not be read`}`,
      foot2:
        'Effective-dated, and superseded versions stay: a self-billing invoice issued in March reproduces against March’s position rather than today’s, and a list of only the current one cannot explain a document somebody is holding. The tax identifier is a number printed on an invoice, not a credential.',
    })
  }

  // The documents those positions were used to raise. Beside the table whose
  // own footer promises them, and read from the partner because that is the
  // only route the backend serves them on.
  const invoices = await partnerInvoices(api, wanted)

  if (!invoices.ok) {
    missing.push('the self-billing invoices issued to this partner')
    // A note, not merely a provenance entry. Nothing renders a row-opened
    // read's provenance, so an operator told only that way is told nothing.
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Self-billing invoices',
      title: `The invoices issued to partner ${wanted} could not be read`,
      body: `${invoices.reason} Whether any document has been raised for this partner is unknown from this read — which is a different fact from none having been raised, and must not be read as one.`,
      ref: invoices.ref,
    })
  } else if (invoices.invoices.length === 0) {
    blocks.push({
      t: 'note',
      tone: 'n',
      kicker: 'Self-billing invoices',
      title: `No self-billing invoice has been issued to partner ${wanted}`,
      body: 'The list was read and it came back empty. A document is only raised once a commission payout has been executed at the institution — it records a payment that happened rather than authorising one — so a partner whose payouts are still awaiting approval, or who has never been paid, has none. Nothing is degraded and nothing is refusing: this is an answer, not a gap.',
      ref: `partner ${wanted} · the read returned no invoices`,
    })
  } else {
    const rows: (readonly Cell[])[] = []
    const detail: Block[] = []
    let bad = 0
    for (const entry of invoices.invoices) {
      const row = invoiceRow(entry)
      if (row === null || !isObject(entry)) {
        bad += 1
        continue
      }
      rows.push(row)
      detail.push(...invoiceDetail(entry))
    }
    blocks.push({
      t: 'table',
      title: `Self-billing invoices — partner ${wanted}`,
      src: 'ledger',
      facets: false,
      cols: INVOICE_COLS,
      rows,
      footer: `${String(rows.length)} invoice(s)${bad === 0 ? '' : ` · ${String(bad)} could not be read`}`,
      foot2:
        'Newest first, as the backend returned them; nothing here re-sorts. The gross equals the payout exactly — the payment was executed at the institution before the document could exist, so any VAT is contained within that amount rather than added to it. The digest is the SHA-256 of the stored rendering: the document itself is not re-drawn here, because a second rendering could differ from the copy the partner holds and the digest is what that copy is checked against.',
    })
    blocks.push(...detail)
  }

  return {
    blocks,
    provenance: missing.length === 0
      ? { kind: 'live', source: PARTNER_SOURCE, authority: AUTHORITY, readAt: readAtStamp(new Date()) }
      : { kind: 'partial', source: PARTNER_SOURCE, authority: AUTHORITY, readAt: readAtStamp(new Date()), missing },
  }
}

// ---------------------------------------------------------------------------
// rf-schemes — the commission scheme history
// ---------------------------------------------------------------------------

/**
 * Every version of every commission scheme, read from
 * `GET /v1/admin/referral/schemes`.
 *
 * # The history IS the screen, not a filter on it
 *
 * "There is no operation that changes a term: editing is publishing a new
 * version, which is what makes a commission accrued in January explainable in
 * July." A commission accrued under v3 is explained by v3 and by nothing else,
 * so a list of only the live versions would hide the terms every past accrual
 * was calculated under — on the one screen somebody opens to explain a figure a
 * partner is disputing.
 *
 * # `rev_share_duration` decides what `rev_share_months` means
 *
 * `none`, `months` and `lifetime` are three different obligations, and the
 * month count means something only under the middle one. Rendering a months
 * figure beside a `lifetime` scheme would understate a commitment that does not
 * end.
 *
 * # Read-only, for the same reason the price lists are
 */
export const SCHEMES_PATH = '/referral/schemes'

const SCHEMES_SOURCE = 'postgres · referral_schemes (GET /v1/admin/referral/schemes)'

const SCHEME_COLS: readonly Column[] = [
  ['Code', 'm', 'minmax(150px,1.1fr)'],
  ['Version', 'm', '90px'],
  ['Name', 'x', 'minmax(160px,1.2fr)'],
  ['Activation bounty', 'a', '160px'],
  ['Qualifies at', 'a', '160px'],
  ['Revenue share', 'x', 'minmax(180px,1.3fr)'],
  ['Default for', 'x', '150px'],
  ['Effective from', 'm', '130px'],
]

function schemeRow(raw: Record<string, unknown>): readonly Cell[] {
  const duration = text(raw['rev_share_duration'])
  const bps = whole(raw['rev_share_bps'])
  const months = whole(raw['rev_share_months'])
  const share = duration === 'none'
    ? 'none'
    : duration === 'lifetime'
      ? `${bps === null ? '— no rate' : `${String(bps)} bps`} · for the life of the customer`
      : duration === 'months'
        ? `${bps === null ? '— no rate' : `${String(bps)} bps`} · ${months === null ? 'for an unstated number of months' : `for ${String(months)} month(s)`}`
        : duration === '' ? '— not stated' : `unknown duration: ${duration}`
  return [
    text(raw['code']) === '' ? '— no code' : text(raw['code']),
    whole(raw['version']) === null ? '—' : `v${String(whole(raw['version']))}`,
    text(raw['display_name']) === '' ? '— no name' : text(raw['display_name']),
    amountCell(raw['activation_bounty']),
    // What "qualified activation" costs: signup alone never pays.
    amountCell(raw['qualified_activation_min']),
    share,
    raw['is_default'] === true
      ? `the default${text(raw['default_for_partner_type']) === '' ? '' : ` for ${text(raw['default_for_partner_type'])}`}`
      : text(raw['default_for_partner_type']) === '' ? '—' : text(raw['default_for_partner_type']),
    day(text(raw['effective_from'])) === '' ? '— not stated' : day(text(raw['effective_from'])),
  ]
}

export function composeSchemes(payload: Record<string, unknown>, readAt: Date): ScreenData {
  const raw = Array.isArray(payload['schemes']) ? (payload['schemes'] as readonly unknown[]) : []
  const rows: (readonly Cell[])[] = []
  const keys: (string | null)[] = []
  let unreadable = 0
  for (const entry of raw) {
    if (!isObject(entry) || text(entry['code']) === '') { unreadable += 1; continue }
    rows.push(schemeRow(entry))
    keys.push(`${text(entry['code'])}@${String(whole(entry['version']) ?? 0)}`)
  }

  const blocks: Block[] = [{
    t: 'table',
    title: 'referral_schemes · every version',
    src: 'ledger',
    facets: false,
    cols: SCHEME_COLS,
    rows,
    footer: `${String(rows.length)} scheme version(s)${unreadable === 0 ? '' : ` · ${String(unreadable)} could not be read`}`,
    foot2:
      'Superseded versions are the point of this screen rather than clutter: a commission accrued in January is explained by the version in force then and by nothing else, and this is the screen somebody opens to explain a figure a partner is disputing. Signing up alone never pays — the activation bounty is earned at the qualifying amount beside it.',
  }]

  if (rows.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Commission schemes',
      title: 'No scheme version has been published',
      body: 'Nothing accrues under an unpublished scheme, so a partner referred today earns nothing — which is a configuration state rather than a fault, and one somebody should have chosen deliberately.',
      ref: 'referral_schemes · the read returned no versions',
    })
  }

  if (unreadable > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Partial read',
      title: `${String(unreadable)} scheme version(s) could not be read`,
      body: 'Counted rather than dropped. A missing version is a set of terms some past commission was calculated under and nobody can now quote.',
      ref: 'a version with no code names no scheme and is refused',
    })
  }

  return {
    blocks,
    provenance: { kind: 'live', source: SCHEMES_SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt) },
    rowKeys: keys,
  }
}

export const loadReferralSchemes = referralLoader(
  'Commission schemes', SCHEMES_PATH, SCHEMES_SOURCE, composeSchemes,
  (api, caller, payload) => [schemeForm(
    api, caller,
    schemeBaseline(Array.isArray(payload['schemes']) ? payload['schemes'] : []),
  )],
)

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/**
 * The permissions these controls need, and the roles that actually hold them.
 *
 * Named as a pair because law 18 wants the ROLE said out loud. Nobody can ask
 * for `referral.scheme.write`; they can ask to be added to Growth.
 *
 * Growth alone holds the scheme permission — the brief gives it to them
 * explicitly, and Ops and Compliance are deliberately not granted it. Conduct
 * is Ops and Growth, so Compliance holds it through Ops and Support does not.
 */
const PERM_SCHEME_WRITE = 'referral.scheme.write'
const PERM_CONDUCT_WRITE = 'referral.conduct.write'

/** `referral.Type` — the partner kinds a scheme can be the default for. */
const PARTNER_TYPES: readonly (readonly [string, string])[] = [
  ['marketer', 'marketer'],
  ['agency', 'agency'],
  ['creator_peer', 'creator_peer'],
]

/** `referral.RevShareDuration`. A closed set of three, and no default. */
const REV_SHARE_DURATIONS: readonly (readonly [string, string])[] = [
  ['none', 'none — no revenue share'],
  ['months', 'months — for a fixed number of months'],
  ['lifetime', 'lifetime — for as long as the referred account earns'],
]

/** `referral.FlagSignal`. */
const FLAG_SIGNALS: readonly (readonly [string, string])[] = [
  ['self_referral', 'self_referral'],
  ['shared_device', 'shared_device'],
  ['velocity', 'velocity'],
  ['incentivised_signup', 'incentivised_signup'],
  ['misleading_promotion', 'misleading_promotion'],
  ['chargeback_cluster', 'chargeback_cluster'],
]

/**
 * Currencies a scheme's amounts may be published in.
 *
 * Not every ISO-4217 code: the backend refuses a scheme whose amounts are in a
 * currency the programme does not operate in, and offering the full table would
 * put the refusal after the typing rather than before it.
 */
const SCHEME_CURRENCIES: readonly (readonly [string, string])[] = [
  ['EUR', 'EUR'], ['GBP', 'GBP'], ['USD', 'USD'],
]

/**
 * Publishing a commission scheme version.
 *
 * # Why this is a create and not an edit, on a screen full of versions
 *
 * There is no operation that changes a published term, and the backend refuses
 * PUT on this path in so many words: a scheme version is immutable and a change
 * is a new version that closes the incumbent at the new boundary — "changes
 * apply to future accruals only" (invariant 9, FR-15.7). That is law 6 with the
 * backend enforcing it, and it is why this control is `versioned`: it labels
 * itself *Save as new version*, requires the effective date, and shows what
 * differs from the version in force.
 *
 * # The currency is one field, not three
 *
 * The handler reads `currency` once and applies it to the bounty, the
 * qualifying minimum and both caps, precisely so a scheme whose amounts
 * disagree cannot be composed. Three currency pickers here would re-introduce
 * the state the handler was written to make impossible, so the amounts take
 * their currency from the one selector — and say "currency not chosen" until it
 * is, rather than assuming euros.
 */
function schemeForm(api: ApiClient, caller: Caller, live: SchemeBaseline | null): FormBlock {
  const blocked = writeRefusal(
    caller, PERM_SCHEME_WRITE, 'Growth', 'Publishing a commission scheme version',
  )

  return {
    t: 'form',
    title: 'Publish a scheme version',
    lead: 'A published version is immutable. This composes the version that supersedes it, and the terms below govern accruals from the effective date onwards — never the ones already earned.',
    versioned: true,
    ...(live === null ? {} : { baseline: live.baseline }),
    fields: [
      {
        name: 'code', label: 'Code', kind: 'text', required: true, mono: true,
        ...(live === null ? {} : { value: live.code }),
        note: 'The scheme this version belongs to. An existing code publishes a new version of it; a new code starts a scheme.',
      },
      {
        name: 'display_name', label: 'Display name', kind: 'text', required: true,
        ...(live === null ? {} : { value: live.displayName }),
      },
      {
        name: 'currency', label: 'Currency', kind: 'select', required: true,
        options: SCHEME_CURRENCIES,
        note: 'Applied to every amount below. One scheme, one currency.',
      },
      {
        name: 'activation_bounty_minor', label: 'Activation bounty', kind: 'amount',
        required: true, currencyFrom: 'currency',
        note: 'Paid when a referred account qualifies. Signing up alone never pays.',
      },
      {
        name: 'qualified_activation_min_minor', label: 'Qualifies at', kind: 'amount',
        required: true, currencyFrom: 'currency',
        note: 'What the referred account must move before the bounty is earned.',
      },
      {
        name: 'rev_share_bps', label: 'Revenue share (bps)', kind: 'text', required: true,
        mono: true, placeholder: '0',
      },
      {
        name: 'rev_share_duration', label: 'Revenue share runs for', kind: 'select',
        required: true, options: REV_SHARE_DURATIONS,
      },
      {
        name: 'rev_share_months', label: 'Months', kind: 'text', required: false, mono: true,
        note: 'Only read when the duration above is `months`.',
      },
      {
        name: 'is_default', label: 'The default scheme', kind: 'checkbox', required: false,
        note: 'Partners with no assignment accrue under the default.',
      },
      {
        name: 'default_for_partner_type', label: 'Default for partner type', kind: 'select',
        required: false, options: PARTNER_TYPES,
      },
    ],
    reasons: SCHEME_REASONS,
    reasonNote: 'Recorded against the version and shown wherever the version explains a commission.',
    auditLine: 'admin.referral.scheme.publish · actor · referral_scheme:<code> · reason',
    cta: 'Publish version',
    ctaNote: 'Accruals already earned are unaffected.',
    blocked,
    ...(blocked === '' ? { submit: schemeSubmit(api) } : {}),
  }
}

/**
 * The reason codes offered for a scheme publication.
 *
 * A short managed list rather than free text (law 21). These are read years
 * later by somebody explaining a commission to a partner who is disputing it,
 * and "updated terms" is not an explanation.
 */
const SCHEME_REASONS: readonly (readonly [string, string, string])[] = [
  ['scheme_launch', 'A new scheme begins', ''],
  ['rate_change', 'The commission rate changes', ''],
  ['cap_change', 'A cap changes', ''],
  ['qualification_change', 'What counts as a qualifying activation changes', ''],
  ['correction', 'A published version stated the wrong terms', ''],
  ['programme_close', 'The programme stops paying under this scheme', ''],
]

function schemeSubmit(api: ApiClient) {
  return (v: Readonly<Record<string, string>>) => submitWrite(
    () => api.post<unknown>(SCHEMES_PATH, {
      code: v['code'],
      display_name: v['display_name'],
      currency: v['currency'],
      // Minor units travel as the STRING the operator typed. The backend reads
      // the quoted form as canonical, and routing this through Number() is how
      // a value a double cannot hold exactly becomes a different amount.
      activation_bounty_minor: v['activation_bounty_minor'],
      qualified_activation_min_minor: v['qualified_activation_min_minor'],
      rev_share_bps: numberOrZero(v['rev_share_bps']),
      rev_share_duration: v['rev_share_duration'],
      rev_share_months: numberOrZero(v['rev_share_months']),
      is_default: v['is_default'] === 'true',
      default_for_partner_type: v['default_for_partner_type'] ?? '',
      effective_from: isoInstant(v['effective_from']),
      reason: v['reason'],
    }),
    'The scheme version was published',
    (answer) => schemeLines(answer),
  )
}

/**
 * A bps or month count as the integer the backend expects.
 *
 * These are counts rather than amounts — law 9 is about money, and a basis
 * point is not money — so `number` is the right carrier and the field is a
 * plain text input rather than an `amount`. A blank is zero here because the
 * backend reads a missing integer as zero anyway, and pretending otherwise
 * would refuse a form the server would have accepted.
 */
function numberOrZero(raw: string | undefined): number {
  if (raw === undefined || raw.trim() === '') return 0
  const n = Number(raw.trim())
  return Number.isSafeInteger(n) ? n : 0
}

/**
 * A date input's `YYYY-MM-DD` as the instant the backend parses.
 *
 * Midnight UTC, stated rather than local: a scheme that starts "on the first"
 * starts at the same moment for everybody reading this console, and letting the
 * operator's timezone decide would make the boundary depend on who published it.
 */
function isoInstant(day: string | undefined): string {
  if (day === undefined || day === '') return ''
  return `${day}T00:00:00Z`
}

function schemeLines(answer: unknown): readonly FormLine[] {
  if (!isObject(answer)) return []
  const lines: FormLine[] = []
  const code = text(answer['code'])
  const version = whole(answer['version'])
  if (code !== '') lines.push(['scheme', code, ''])
  if (version !== null) lines.push(['version', String(version), 'the version this published'])
  const from = text(answer['effective_from'])
  if (from !== '') lines.push(['effective from', from, 'governs accruals from here'])
  return lines
}

/** What is live now, for law 6's diff. */
interface SchemeBaseline {
  readonly code: string
  readonly displayName: string
  readonly baseline: readonly (readonly [string, string])[]
}

/**
 * The version in force, as the thing a new version is composed against.
 *
 * The newest row the read returned, which is what this endpoint orders by. A
 * catalogue with no versions has no baseline and the form starts empty — there
 * is nothing to diff against, and inventing a baseline of zeros would render a
 * diff claiming the operator had changed every term.
 */
function schemeBaseline(rows: readonly unknown[]): SchemeBaseline | null {
  const first = rows.find(isObject)
  if (first === undefined) return null
  const code = text(first['code'])
  if (code === '') return null
  const displayName = text(first['display_name'])
  return {
    code,
    displayName,
    baseline: [
      ['Code', code],
      ['Display name', displayName],
      ['Revenue share (bps)', String(whole(first['rev_share_bps']) ?? 0)],
    ],
  }
}

/**
 * Raising a fraud flag.
 *
 * Not versioned: a flag is an observation somebody made at a moment, not a term
 * that governs. It is answered by a resolution rather than superseded, which is
 * why the resolve control below is a separate act with its own outcome.
 *
 * There is no field for who raised it. The backend takes the raiser from the
 * authenticated session and says so — "there is no shape of request that files
 * a flag under somebody else's name" — and a console field for it would be a
 * field the server ignores.
 */
function raiseFlagForm(api: ApiClient, caller: Caller): FormBlock {
  const blocked = writeRefusal(
    caller, PERM_CONDUCT_WRITE, 'Ops', 'Raising a fraud flag',
  )

  return {
    t: 'form',
    title: 'Raise a flag',
    lead: 'A flag questions a partner’s conduct. It suspends nothing on its own — accrued balances are preserved while it is open, and only a resolution changes what the partner is owed.',
    fields: [
      {
        name: 'partner_id', label: 'Partner id', kind: 'text', required: true, mono: true,
        note: 'From the partner row this concerns.',
      },
      {
        name: 'code_id', label: 'Code id', kind: 'text', required: false, mono: true,
        note: 'The referral code the signal was seen on, where it is one code rather than the partner.',
      },
      { name: 'signal', label: 'Signal', kind: 'select', required: true, options: FLAG_SIGNALS },
      {
        name: 'detail', label: 'Detail', kind: 'textarea', required: true,
        note: 'What was observed. Read by whoever resolves this, who will not have your screen in front of them.',
      },
    ],
    reasons: FLAG_RAISE_REASONS,
    reasonNote: 'Recorded on the flag and in the audit row.',
    auditLine: 'admin.referral.flag.raise · actor · referral_partner:<id> · reason',
    cta: 'Raise flag',
    ctaNote: 'Accrued balances are preserved while the flag is open.',
    blocked,
    ...(blocked === '' ? { submit: raiseFlagSubmit(api) } : {}),
  }
}

const FLAG_RAISE_REASONS: readonly (readonly [string, string, string])[] = [
  ['monitoring_alert', 'An automated signal fired', ''],
  ['manual_review', 'Found while reviewing the programme', ''],
  ['partner_report', 'Reported by another partner', ''],
  ['customer_report', 'Reported by a customer', ''],
  ['finance_query', 'Raised out of a finance reconciliation', ''],
]

function raiseFlagSubmit(api: ApiClient) {
  return (v: Readonly<Record<string, string>>) => submitWrite(
    () => api.post<unknown>(FLAGS_PATH, {
      partner_id: numberOrZero(v['partner_id']),
      code_id: numberOrZero(v['code_id']),
      signal: v['signal'],
      detail: v['detail'],
      reason: v['reason'],
    }),
    'The flag was raised',
    (answer) => (isObject(answer) && whole(answer['id']) !== null
      ? [['flag', String(whole(answer['id'])), 'now open on this partner']]
      : []),
  )
}

/**
 * Resolving a flag that is open.
 *
 * # The outcome is required and has no default, and that is the backend's rule
 *
 * `confirmed` and `dismissed` are opposite conclusions about somebody's income,
 * and the handler refuses a body omitting the outcome rather than defaulting to
 * the safer-looking one. The select offers no pre-selected option for the same
 * reason `Decide` pre-selects no radio.
 *
 * # The flag is chosen from the queue rather than typed
 *
 * The options are exactly the open flags on screen. Typing an id would let an
 * operator resolve a flag they are not looking at — including one raised since
 * this page was read — and the two are indistinguishable from a number.
 */
function resolveFlagForm(api: ApiClient, caller: Caller, open: readonly OpenFlag[]): FormBlock {
  const blocked = open.length === 0
    ? 'No flag is open, so there is nothing to resolve.'
    : writeRefusal(caller, PERM_CONDUCT_WRITE, 'Ops', 'Resolving a fraud flag')

  return {
    t: 'form',
    title: 'Resolve a flag',
    lead: 'Confirming a flag stands the finding up against the partner; dismissing it clears them. Both are recorded and neither is reversible from here.',
    fields: [
      {
        name: 'flag_id', label: 'Flag', kind: 'select', required: true,
        options: open.map((f) => [f.id, f.label] as const),
      },
      {
        name: 'outcome', label: 'Outcome', kind: 'select', required: true,
        options: [
          ['confirmed', 'confirmed — the conduct stands as found'],
          ['dismissed', 'dismissed — the partner is cleared'],
        ],
        note: 'Required, and there is no default. The two are opposite conclusions about somebody’s income.',
      },
    ],
    reasons: FLAG_RESOLVE_REASONS,
    reasonNote: 'Read later by whoever explains this to the partner.',
    auditLine: 'admin.referral.flag.resolve · actor · referral_fraud_flag:<id> · reason',
    cta: 'Resolve flag',
    ctaNote: 'The partner’s accrued balance follows the outcome.',
    blocked,
    ...(blocked === '' ? { submit: resolveFlagSubmit(api) } : {}),
  }
}

const FLAG_RESOLVE_REASONS: readonly (readonly [string, string, string])[] = [
  ['evidence_sufficient', 'The evidence supports the signal', 'confirmed'],
  ['pattern_confirmed', 'The pattern was confirmed across accounts', 'confirmed'],
  ['partner_admitted', 'The partner accepted the finding', 'confirmed'],
  ['evidence_insufficient', 'The evidence does not support the signal', 'dismissed'],
  ['false_positive', 'The signal fired on legitimate activity', 'dismissed'],
  ['partner_explained', 'The partner accounted for it', 'dismissed'],
]

function resolveFlagSubmit(api: ApiClient) {
  return (v: Readonly<Record<string, string>>) => submitWrite(
    () => api.post<unknown>(`${FLAGS_PATH}/${String(v['flag_id'])}/resolve`, {
      outcome: v['outcome'],
      reason: v['reason'],
    }),
    'The flag was resolved',
    (answer) => (isObject(answer)
      ? [['state', text(answer['state']), 'as the backend recorded it']]
      : []),
  )
}

/** An open flag, as an option on the resolve control. */
interface OpenFlag {
  readonly id: string
  readonly label: string
}

function openFlags(rows: readonly unknown[]): readonly OpenFlag[] {
  const out: OpenFlag[] = []
  for (const raw of rows) {
    if (!isObject(raw)) continue
    const id = whole(raw['id'])
    if (id === null) continue
    const signal = text(raw['signal'])
    const partner = text(raw['partner_display_name']) || text(raw['partner_name'])
    out.push({
      id: String(id),
      label: `${String(id)} — ${signal === '' ? 'signal not stated' : signal}${partner === '' ? '' : ` · ${partner}`}`,
    })
  }
  return out
}
